/**
 * Round-level tie handling. Ties always split points automatically — this is not
 * configurable (spec §6). Two players tied for 1st in [10,6,4,2] each get (10+6)/2=8;
 * 3rd/4th still get 4 and 2. The historical team-tie convention (5.5 = (8+3)/2) is the
 * same rule applied to a two-entry table [win, loss].
 */
export function splitPlacementPoints<Id extends string>(
  entries: { id: Id; value: number }[],
  pointTable: number[],
  opts: { lowerIsBetter: boolean } = { lowerIsBetter: true },
): Map<Id, number> {
  const sorted = [...entries].sort((a, b) =>
    opts.lowerIsBetter ? a.value - b.value : b.value - a.value,
  );

  const points = new Map<Id, number>();
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1]!.value === sorted[i]!.value) j++;

    const slice = pointTable.slice(i, j + 1);
    while (slice.length < j - i + 1) slice.push(0);
    const avg = slice.reduce((sum, v) => sum + v, 0) / slice.length;

    for (let k = i; k <= j; k++) points.set(sorted[k]!.id, avg);
    i = j + 1;
  }

  return points;
}

/** Two-team match result: win/loss points, or the average of both on a tie. */
export function splitTeamPoints(
  a: { id: string; value: number },
  b: { id: string; value: number },
  teamPoints: { win: number; loss: number },
  opts: { lowerIsBetter: boolean } = { lowerIsBetter: true },
): Map<string, number> {
  return splitPlacementPoints([a, b], [teamPoints.win, teamPoints.loss], opts);
}
